import React from 'react';
import PropTypes from 'prop-types';
import { useUserBehaviorAnalytics } from '../../hooks/useAnalytics';
import MetricCard from '../dashboard/core/MetricCard';
import MetricSection from '../dashboard/core/MetricSection';
import BarChart from '../ui/BarChart';
import PieChart from '../ui/PieChart';
import { formatPercent } from '../../utils/format';
import { FILTER_PERIODS } from '../../constants';
import './UserBehavior.css';

/**
 * User Behavior Component
 * Analyzes feature adoption, usage patterns, and user preferences
 */
const UserBehavior = ({ period = FILTER_PERIODS.ALL }) => {
  const { data, loading, error } = useUserBehaviorAnalytics(period);

  if (error) {
    return (
      <div className="user-behavior error">
        <h3>Error loading user behavior data</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="user-behavior metric-loading" aria-live="polite" aria-busy="true">
        <div className="loading-spinner" />
        <p>Loading user behavior data...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="user-behavior no-data-message">
        <i className="bi bi-info-circle icon-brand"></i>
        <p>No user behavior data available for this period.</p>
      </div>
    );
  }

  const { featureAdoption, usagePatterns, preferences, socialGraph, summary } = data;

  // Feature adoption metrics
  const adoptionMetrics = [
    {
      id: 'splits',
      title: 'Split Creators',
      value: featureAdoption.splits.totalCreators,
      formatType: 'number',
      subtitle: formatPercent(featureAdoption.splits.adoptionRate, 1),
      color: 'purple'
    },
    {
      id: 'badges',
      title: 'Badge Earners',
      value: featureAdoption.badges.totalEarners,
      formatType: 'number',
      subtitle: formatPercent(featureAdoption.badges.adoptionRate, 1),
      color: 'yellow'
    },
    {
      id: 'wallets',
      title: 'Wallet Users',
      value: featureAdoption.wallets.totalUsers,
      formatType: 'number',
      subtitle: formatPercent(featureAdoption.wallets.adoptionRate, 1),
      color: 'teal'
    },
    {
      id: 'phone-auth',
      title: 'Phone Auth Users',
      value: featureAdoption.phoneAuth.totalUsers,
      formatType: 'number',
      subtitle: formatPercent(featureAdoption.phoneAuth.adoptionRate, 1),
      color: 'blue'
    }
  ];

  // Usage pattern metrics
  const usageMetrics = [
    {
      id: 'avg-participants',
      title: 'Avg Participants',
      value: usagePatterns.avgParticipants,
      formatType: 'number',
      subtitle: 'per split',
      color: 'blue'
    },
    {
      id: 'avg-split-value',
      title: 'Avg Split Value',
      value: usagePatterns.avgSplitValue,
      formatType: 'currency',
      color: 'green'
    },
    {
      id: 'fair-splits',
      title: 'Fair Splits',
      value: usagePatterns.splitTypes.fair,
      formatType: 'number',
      subtitle: formatPercent(usagePatterns.splitTypes.fairPercentage, 1),
      color: 'teal'
    },
    {
      id: 'degen-splits',
      title: 'Degen Splits',
      value: usagePatterns.splitTypes.degen,
      formatType: 'number',
      subtitle: formatPercent(usagePatterns.splitTypes.degenPercentage, 1),
      color: 'purple'
    }
  ];

  // Social graph metrics
  const socialMetrics = [
    {
      id: 'total-contacts',
      title: 'Total Contacts',
      value: socialGraph.totalContacts,
      formatType: 'number',
      color: 'blue'
    },
    {
      id: 'avg-contacts',
      title: 'Avg Contacts/User',
      value: socialGraph.avgContactsPerUser,
      formatType: 'number',
      color: 'cyan'
    },
    {
      id: 'favorite-contacts',
      title: 'Favorite Contacts',
      value: socialGraph.favoriteContacts,
      formatType: 'number',
      color: 'yellow'
    },
    {
      id: 'contacts-with-wallets',
      title: 'Contacts w/ Wallets',
      value: socialGraph.contactsWithWallets,
      formatType: 'number',
      color: 'teal'
    }
  ];

  // Split type preference chart
  const splitTypeChartData = [
    { label: 'Fair', value: usagePatterns.splitTypes.fair },
    { label: 'Degen', value: usagePatterns.splitTypes.degen }
  ];

  return (
    <div className="user-behavior">
      <div className="behavior-summary">
        <MetricCard
          title="Total Users"
          value={summary.totalUsers}
          formatType="number"
          color="blue"
          loading={loading}
        />
      </div>

      <div className="behavior-grid">
        <MetricSection
          title="Feature Adoption"
          icon="bi-star"
          description="User adoption of key features"
          metrics={adoptionMetrics}
          loading={loading}
        />

        <MetricSection
          title="Usage Patterns"
          icon="bi-activity"
          description="How users interact with the app"
          metrics={usageMetrics}
          loading={loading}
        />

        <MetricSection
          title="Social Graph"
          icon="bi-people"
          description="User contact networks and connections"
          metrics={socialMetrics}
          loading={loading}
        />
      </div>

      <div className="preferences-visualization">
        <h3>Split Type Preferences</h3>
        <div className="chart-container">
          <PieChart
            data={splitTypeChartData}
            label="Splits"
            formatType="number"
            color="accent"
          />
        </div>
      </div>
    </div>
  );
};

UserBehavior.propTypes = {
  period: PropTypes.string.isRequired
};

export default UserBehavior;

